import { action, atom } from '@reatom/framework'
import { useAtom } from '@reatom/npm-react'
import { AnimatePresence } from 'motion/react'
import styled from 'styled-components'

import { Deeplink, openDeeplinkDialogAction } from '@root/entities/deeplinking'
import {
  openBuyBoostersFlowEvent,
  openBuyPremiumFlowEvent,
  openExtendPremiumFlowEvent,
} from '@root/entities/premium'
import { isTauriOrBrowserLocalAtom } from '@root/shared'

import { BuyBoostersDrawer } from './buy-boosters-drawer'
import { BuyPremiumDrawer } from './buy-premium-drawer'
import { ExtendPremiumDrawer } from './extend-premium-drawer/extend-premium-drawer'

import type {
  CurrencyModel,
  CurrencyWithPremium,
} from '@root/entities/currency'
import type { PremiumMetaModel } from '@root/entities/premium'

const buyPremiumDrawerParamsAtom = atom<
  { currency: CurrencyModel; premium: PremiumMetaModel | undefined } | undefined
>(undefined, 'buyPremiumDrawerParamsAtom')

const buyBoostersDrawerParamsAtom = atom<
  CurrencyWithPremium.CurrencyWithPremium | undefined
>(undefined, 'buyBoostersDrawerParamsAtom')

const extendBoostersDrawerParamsAtom = atom<
  CurrencyWithPremium.CurrencyWithPremium | undefined
>(undefined, 'extendBoostersDrawerParamsAtom')

const openBuyBoostersFlowAction = action(
  (
    ctx,
    params: {
      currency: CurrencyModel
      premium: PremiumMetaModel
    },
  ) => {
    if (ctx.get(isTauriOrBrowserLocalAtom)) {
      buyBoostersDrawerParamsAtom(ctx, params)
    } else {
      openDeeplinkDialogAction(ctx, {
        deeplink: new Deeplink.CheckCurrency({
          network: params.currency.network,
          queryAddress: params.currency.address,
        }),
      })
    }
  },
  'openBuyBoostersFlowAction',
)

const openBuyPremiumFlowAction = action(
  (
    ctx,
    params: {
      currency: CurrencyModel
      premium: PremiumMetaModel | undefined
    },
  ) => {
    if (ctx.get(isTauriOrBrowserLocalAtom)) {
      buyPremiumDrawerParamsAtom(ctx, params)
    } else {
      openDeeplinkDialogAction(ctx, {
        deeplink: new Deeplink.CheckCurrency({
          network: params.currency.network,
          queryAddress: params.currency.address,
        }),
      })
    }
  },
  'openBuyPremiumFlowAction',
)

openBuyPremiumFlowEvent.onCall((ctx, _, [params]) => {
  openBuyPremiumFlowAction(ctx, params)
})

openBuyBoostersFlowEvent.onCall((ctx, _, [params]) => {
  if (ctx.get(params.premium.premium)) {
    openBuyBoostersFlowAction(ctx, {
      currency: params.currency,
      premium: params.premium,
    })
    return
  }
  openBuyPremiumFlowAction(ctx, params)
})

openExtendPremiumFlowEvent.onCall((ctx, _, [params]) => {
  if (ctx.get(params.premium.isAllowedToExtend)) {
    extendBoostersDrawerParamsAtom(ctx, params)
  }
})

export const PremiumBuyDrawers: React.FC = () => {
  const [premiumDrawerParams, setPremiumDrawerParams] = useAtom(
    buyPremiumDrawerParamsAtom,
  )
  const [boostersDrawerParams, setBoostersDrawerParams] = useAtom(
    buyBoostersDrawerParamsAtom,
  )
  const [extendBoostersDrawerParams, setExtendBoostersDrawerParams] = useAtom(
    extendBoostersDrawerParamsAtom,
  )

  return (
    <Root>
      <AnimatePresence>
        {premiumDrawerParams ? (
          <BuyPremiumDrawer
            currency={premiumDrawerParams.currency}
            premium={premiumDrawerParams.premium}
            open
            onOpenChange={(open) => {
              if (!open) {
                setPremiumDrawerParams(undefined)
              }
            }}
          />
        ) : undefined}
      </AnimatePresence>
      <AnimatePresence>
        {boostersDrawerParams ? (
          <BuyBoostersDrawer
            currency={boostersDrawerParams.currency}
            premium={boostersDrawerParams.premium}
            open
            onOpenChange={(open) => {
              if (!open) {
                setBoostersDrawerParams(undefined)
              }
            }}
          />
        ) : undefined}
      </AnimatePresence>
      <AnimatePresence>
        {extendBoostersDrawerParams?.premium ? (
          <ExtendPremiumDrawer
            currency={extendBoostersDrawerParams.currency}
            premium={extendBoostersDrawerParams.premium}
            open
            onOpenChange={(open) => {
              if (!open) {
                setExtendBoostersDrawerParams(undefined)
              }
            }}
          />
        ) : undefined}
      </AnimatePresence>
    </Root>
  )
}

const Root = styled.div``
